/**
 * Milestone Service
 * Uses HTTP API when enabled, otherwise falls back to localStorage
 */

import axios from 'axios';

const USE_API = process.env.REACT_APP_USE_API !== 'false';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

const MILESTONES_KEY = 'eqaya_milestones';
const MILESTONE_COUNTER_KEY = 'eqaya_milestone_counter';

// Initialize milestone counter
const getMilestoneCounter = () => {
  const counter = localStorage.getItem(MILESTONE_COUNTER_KEY);
  return counter ? parseInt(counter) : 30000;
};

const setMilestoneCounter = (counter) => {
  localStorage.setItem(MILESTONE_COUNTER_KEY, counter.toString());
};

// Utility functions
const getAllMilestones = () => {
  const milestones = localStorage.getItem(MILESTONES_KEY);
  return milestones ? JSON.parse(milestones) : [];
};

const saveMilestones = (milestones) => {
  localStorage.setItem(MILESTONES_KEY, JSON.stringify(milestones));
};

const generateMilestoneId = () => {
  const counter = getMilestoneCounter();
  setMilestoneCounter(counter + 1);
  return `MS_${counter}`;
};

// Simulate network delay
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Default milestone templates for different project types
const MILESTONE_TEMPLATES = {
  residential: [
    {
      name: 'Site Preparation & Foundation',
      type: 'foundation',
      order: 1,
      weight: 15,
      verificationRequired: true,
      verificationScope: [
        'Site clearance and leveling',
        'Foundation excavation depth and dimensions',
        'Concrete pour quality and curing',
        'Reinforcement placement and coverage',
        'Waterproofing application'
      ],
      qualityChecks: [
        'Foundation depth meets building plans',
        'Concrete strength test results',
        'Proper reinforcement spacing',
        'No visible cracks or defects'
      ],
      safetyRequirements: [
        'Excavation safety measures',
        'Proper scaffolding installation',
        'Safety signage and barriers'
      ]
    },
    {
      name: 'Structural Framing',
      type: 'framing',
      order: 2,
      weight: 20,
      verificationRequired: true,
      verificationScope: [
        'Wall framing accuracy and alignment',
        'Roof structure installation',
        'Window and door frame placement',
        'Structural integrity compliance'
      ],
      qualityChecks: [
        'Framing dimensions per architectural plans',
        'Proper joint connections',
        'Level and plumb verification',
        'Structural load calculations compliance'
      ],
      safetyRequirements: [
        'Fall protection systems',
        'Temporary bracing installation',
        'Material handling safety'
      ]
    },
    {
      name: 'Roofing Installation',
      type: 'roofing',
      order: 3,
      weight: 12,
      verificationRequired: true,
      verificationScope: [
        'Roof covering installation',
        'Guttering and downpipes',
        'Roof ventilation systems',
        'Waterproofing integrity'
      ],
      qualityChecks: [
        'Proper roof slope and drainage',
        'No leaks or water penetration',
        'Secure attachment of roofing materials',
        'Ventilation adequacy'
      ],
      safetyRequirements: [
        'Roof safety equipment',
        'Weather protection measures',
        'Safe access provisions'
      ]
    },
    {
      name: 'Electrical Installation',
      type: 'electrical',
      order: 4,
      weight: 15,
      verificationRequired: true,
      verificationScope: [
        'Electrical panel installation',
        'Circuit wiring and connections',
        'Outlet and switch placement',
        'Safety switch functionality',
        'Code compliance verification'
      ],
      qualityChecks: [
        'Electrical load calculations',
        'Proper circuit protection',
        'Grounding system integrity',
        'ZESA compliance certificate'
      ],
      safetyRequirements: [
        'Electrical safety lockout procedures',
        'Insulated tools requirement',
        'Qualified electrician certification'
      ]
    },
    {
      name: 'Plumbing Installation',
      type: 'plumbing',
      order: 5,
      weight: 13,
      verificationRequired: true,
      verificationScope: [
        'Water supply system installation',
        'Drainage and sewerage connections',
        'Fixture installation and testing',
        'Pressure testing completion'
      ],
      qualityChecks: [
        'No leaks in water supply lines',
        'Proper drainage flow and grade',
        'Water pressure adequacy',
        'Hot water system functionality'
      ],
      safetyRequirements: [
        'Water quality testing',
        'Backflow prevention measures',
        'Safe excavation practices'
      ]
    },
    {
      name: 'Interior Finishing',
      type: 'finishing',
      order: 6,
      weight: 20,
      verificationRequired: true,
      verificationScope: [
        'Wall plastering and painting',
        'Flooring installation',
        'Door and window installation',
        'Kitchen and bathroom fitting',
        'Built-in furniture completion'
      ],
      qualityChecks: [
        'Surface finish quality',
        'Color and texture consistency',
        'Proper door and window operation',
        'Fixture alignment and functionality'
      ],
      safetyRequirements: [
        'Ventilation during painting',
        'Safe handling of finishing materials',
        'Dust control measures'
      ]
    },
    {
      name: 'Final Inspection & Handover',
      type: 'final',
      order: 7,
      weight: 5,
      verificationRequired: true,
      verificationScope: [
        'Overall construction quality',
        'All systems functionality',
        'Compliance with building regulations',
        'Punch list completion',
        'Documentation handover'
      ],
      qualityChecks: [
        'All work completed per specifications',
        'No outstanding defects',
        'All certificates and warranties provided',
        'Client walkthrough completion'
      ],
      safetyRequirements: [
        'Final safety inspection',
        'Occupancy certificate verification',
        'Emergency systems testing'
      ]
    }
  ],
  commercial: [
    {
      name: 'Site Development & Foundation',
      type: 'foundation',
      order: 1,
      weight: 18,
      verificationRequired: true,
      verificationScope: ['Site preparation', 'Foundation systems', 'Utilities infrastructure'],
      qualityChecks: ['Engineering specifications compliance', 'Load-bearing capacity verification'],
      safetyRequirements: ['Commercial site safety standards', 'Environmental compliance']
    },
    {
      name: 'Structural Framework',
      type: 'framing',
      order: 2,
      weight: 25,
      verificationRequired: true,
      verificationScope: ['Steel/concrete framework', 'Floor systems', 'Structural connections'],
      qualityChecks: ['Structural engineering approval', 'Load testing completion'],
      safetyRequirements: ['Commercial building safety codes', 'Fire safety systems']
    },
    {
      name: 'Building Envelope',
      type: 'roofing',
      order: 3,
      weight: 15,
      verificationRequired: true,
      verificationScope: ['Roofing systems', 'External cladding', 'Windows and doors'],
      qualityChecks: ['Weather resistance testing', 'Energy efficiency compliance'],
      safetyRequirements: ['Fall protection systems', 'Weather protection']
    },
    {
      name: 'MEP Systems',
      type: 'electrical',
      order: 4,
      weight: 22,
      verificationRequired: true,
      verificationScope: ['Electrical systems', 'Plumbing', 'HVAC installation'],
      qualityChecks: ['Systems integration testing', 'Code compliance verification'],
      safetyRequirements: ['Commercial electrical safety', 'Fire safety systems']
    },
    {
      name: 'Interior Build-out',
      type: 'finishing',
      order: 5,
      weight: 15,
      verificationRequired: true,
      verificationScope: ['Interior partitions', 'Flooring', 'Ceiling systems', 'Finishing'],
      qualityChecks: ['Commercial grade materials', 'Accessibility compliance'],
      safetyRequirements: ['Indoor air quality', 'Fire safety compliance']
    },
    {
      name: 'Final Commission & Handover',
      type: 'final',
      order: 6,
      weight: 5,
      verificationRequired: true,
      verificationScope: ['Systems commissioning', 'Final inspections', 'Documentation'],
      qualityChecks: ['Occupancy certificate', 'All systems operational'],
      safetyRequirements: ['Final safety certification', 'Emergency systems testing']
    }
  ]
};

// Initialize sample milestones
const initializeSampleMilestones = () => {
  const existingMilestones = getAllMilestones();
  if (existingMilestones.length === 0) {
    const sampleMilestones = [
      {
        id: 'MS_001',
        projectId: 'PROJ-1000',
        milestoneDetails: {
          name: 'Foundation Completion',
          description: 'Complete foundation work including excavation, concrete pour, and waterproofing',
          type: 'foundation',
          order: 1,
          weight: 15
        },
        requirements: {
          verificationRequired: true,
          verificationScope: [
            'Foundation excavation depth and dimensions',
            'Concrete pour quality and curing',
            'Reinforcement placement and coverage',
            'Waterproofing application',
            'Compliance with building plans'
          ],
          qualityChecks: [
            'Foundation depth meets specifications (1.2m minimum)',
            'Concrete strength test results (C25 minimum)',
            'Proper reinforcement spacing (200mm centers)',
            'No visible cracks or structural defects'
          ],
          safetyRequirements: [
            'Excavation safety barriers in place',
            'Proper scaffolding and access',
            'Safety signage and warning systems'
          ],
          documentRequirements: [
            'Concrete test certificates',
            'Reinforcement placement photos',
            'Waterproofing application evidence'
          ]
        },
        scheduling: {
          plannedStartDate: '2024-01-15',
          plannedEndDate: '2024-01-24',
          actualStartDate: '2024-01-15',
          actualEndDate: '2024-01-25',
          verificationDeadline: '2024-01-27'
        },
        financial: {
          budgetAllocation: 18000.00,
          actualCost: 17500.00,
          paymentSchedule: [
            {
              percentage: 30,
              trigger: 'start',
              amount: 5400.00,
              status: 'released'
            },
            {
              percentage: 70,
              trigger: 'verification_approved',
              amount: 12600.00,
              status: 'released'
            }
          ]
        },
        verification: {
          required: true,
          requestId: 'VR_001',
          status: 'completed',
          completedAt: '2024-01-25T11:00:00Z',
          score: 95,
          passed: true
        },
        status: 'verified',
        progress: 100,
        createdAt: '2024-01-10T08:00:00Z',
        updatedAt: '2024-01-25T15:00:00Z'
      },
      {
        id: 'MS_002',
        projectId: 'PROJ-1000',
        milestoneDetails: {
          name: 'Structural Framing',
          description: 'Complete wall framing, roof structure, and window/door frame installation',
          type: 'framing',
          order: 2,
          weight: 20
        },
        requirements: {
          verificationRequired: true,
          verificationScope: [
            'Wall framing accuracy and alignment',
            'Roof structure installation',
            'Window and door frame placement',
            'Structural integrity compliance'
          ],
          qualityChecks: [
            'Framing dimensions per architectural plans',
            'Proper joint connections and fastening',
            'Level and plumb verification within tolerance',
            'Structural load calculations compliance'
          ],
          safetyRequirements: [
            'Fall protection systems installed',
            'Temporary bracing in place',
            'Safe material handling procedures'
          ],
          documentRequirements: [
            'Framing inspection photos',
            'Structural engineer approval',
            'Material compliance certificates'
          ]
        },
        scheduling: {
          plannedStartDate: '2024-01-26',
          plannedEndDate: '2024-02-10',
          actualStartDate: '2024-01-26',
          actualEndDate: null,
          verificationDeadline: '2024-02-12'
        },
        financial: {
          budgetAllocation: 24000.00,
          actualCost: 0.00,
          paymentSchedule: [
            {
              percentage: 25,
              trigger: 'start',
              amount: 6000.00,
              status: 'released'
            },
            {
              percentage: 75,
              trigger: 'verification_approved',
              amount: 18000.00,
              status: 'pending'
            }
          ]
        },
        verification: {
          required: true,
          requestId: null,
          status: 'pending',
          completedAt: null,
          score: null,
          passed: null
        },
        status: 'in_progress',
        progress: 65,
        createdAt: '2024-01-10T08:00:00Z',
        updatedAt: '2024-01-28T12:00:00Z'
      },
      {
        id: 'MS_003',
        projectId: 'PROJ-1001',
        milestoneDetails: {
          name: 'Electrical Installation',
          description: 'Complete electrical wiring, panel installation, and safety systems',
          type: 'electrical',
          order: 1,
          weight: 100 // Single milestone project
        },
        requirements: {
          verificationRequired: true,
          verificationScope: [
            'Electrical panel installation and configuration',
            'Circuit wiring and connections',
            'Outlet and switch placement verification',
            'Safety switch functionality testing',
            'Code compliance and ZESA approval'
          ],
          qualityChecks: [
            'Electrical load calculations verified',
            'Proper circuit protection installed',
            'Grounding system integrity confirmed',
            'ZESA compliance certificate obtained'
          ],
          safetyRequirements: [
            'Electrical safety lockout procedures',
            'Insulated tools and equipment',
            'Qualified electrician certification'
          ],
          documentRequirements: [
            'Electrical installation certificate',
            'ZESA approval documentation',
            'Circuit testing results'
          ]
        },
        scheduling: {
          plannedStartDate: '2024-01-20',
          plannedEndDate: '2024-01-26',
          actualStartDate: '2024-01-20',
          actualEndDate: null,
          verificationDeadline: '2024-01-28'
        },
        financial: {
          budgetAllocation: 12000.00,
          actualCost: 0.00,
          paymentSchedule: [
            {
              percentage: 40,
              trigger: 'start',
              amount: 4800.00,
              status: 'released'
            },
            {
              percentage: 60,
              trigger: 'verification_approved',
              amount: 7200.00,
              status: 'pending'
            }
          ]
        },
        verification: {
          required: true,
          requestId: 'VR_002',
          status: 'pending',
          completedAt: null,
          score: null,
          passed: null
        },
        status: 'in_progress',
        progress: 80,
        createdAt: '2024-01-15T09:00:00Z',
        updatedAt: '2024-01-26T14:00:00Z'
      }
    ];
    saveMilestones(sampleMilestones);
  }
};

// Milestone service
const milestoneService = {

  /**
   * Upload files a contractor attaches to a completion report (photos,
   * videos, PDFs, office documents, 3D/CAD designs). Returns
   * [{ url, name, type, size }] — stored on the report as-is.
   */
  uploadReportFiles: async (files) => {
    const form = new FormData();
    Array.from(files).forEach((f) => form.append('files', f));
    const response = await apiClient.post('/uploads/report-files', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.data?.files || [];
  },

  // Initialize service
  initialize: () => {
    initializeSampleMilestones();
  },

  // Create milestones from template
  createMilestonesFromTemplate: async (projectId, projectType = 'residential', customizations = {}) => {
    if (USE_API) {
      // In API mode, milestone templates should be created server-side
      const { data } = await apiClient.post(`/projects/${projectId}/milestones/template`, {
        projectType,
        customizations
      });
      return data;
    }
    
    const template = MILESTONE_TEMPLATES[projectType];
    if (!template) {
      throw new Error(`Unknown project type: ${projectType}`);
    }
    
    const existingMilestones = getAllMilestones();
    const newMilestones = [];
    
    for (let templateMilestone of template) {
      const milestone = {
        id: generateMilestoneId(),
        projectId: projectId,
        milestoneDetails: {
          name: customizations[templateMilestone.type]?.name || templateMilestone.name,
          description: customizations[templateMilestone.type]?.description || 
                      `Complete ${templateMilestone.name.toLowerCase()} according to project specifications`,
          type: templateMilestone.type,
          order: templateMilestone.order,
          weight: customizations[templateMilestone.type]?.weight || templateMilestone.weight
        },
        requirements: {
          verificationRequired: customizations[templateMilestone.type]?.verificationRequired ?? templateMilestone.verificationRequired,
          verificationScope: customizations[templateMilestone.type]?.verificationScope || templateMilestone.verificationScope,
          qualityChecks: customizations[templateMilestone.type]?.qualityChecks || templateMilestone.qualityChecks,
          safetyRequirements: customizations[templateMilestone.type]?.safetyRequirements || templateMilestone.safetyRequirements,
          documentRequirements: customizations[templateMilestone.type]?.documentRequirements || []
        },
        scheduling: {
          plannedStartDate: customizations[templateMilestone.type]?.plannedStartDate || null,
          plannedEndDate: customizations[templateMilestone.type]?.plannedEndDate || null,
          actualStartDate: null,
          actualEndDate: null,
          verificationDeadline: null
        },
        financial: {
          budgetAllocation: customizations[templateMilestone.type]?.budgetAllocation || 0,
          actualCost: 0,
          paymentSchedule: customizations[templateMilestone.type]?.paymentSchedule || [
            {
              percentage: 30,
              trigger: 'start',
              amount: 0,
              status: 'pending'
            },
            {
              percentage: 70,
              trigger: 'verification_approved',
              amount: 0,
              status: 'pending'
            }
          ]
        },
        verification: {
          required: templateMilestone.verificationRequired,
          requestId: null,
          status: templateMilestone.verificationRequired ? 'not_required' : 'not_required',
          completedAt: null,
          score: null,
          passed: null
        },
        status: 'not_started',
        progress: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      newMilestones.push(milestone);
    }
    
    existingMilestones.push(...newMilestones);
    saveMilestones(existingMilestones);
    
    return {
      success: true,
      data: newMilestones,
      message: `Created ${newMilestones.length} milestones for ${projectType} project`
    };
  },

  // Create custom milestone
  createMilestone: async (milestoneData) => {
    if (USE_API) {
      const { data } = await apiClient.post('/milestones', milestoneData);
      return data;
    }
    
    // Validate required fields
    const requiredFields = ['projectId', 'milestoneDetails'];
    for (let field of requiredFields) {
      if (!milestoneData[field]) {
        throw new Error(`${field} is required.`);
      }
    }
    
    if (!milestoneData.milestoneDetails.name || !milestoneData.milestoneDetails.type) {
      throw new Error('Milestone name and type are required.');
    }
    
    const newMilestone = {
      id: generateMilestoneId(),
      projectId: milestoneData.projectId,
      milestoneDetails: {
        name: milestoneData.milestoneDetails.name,
        description: milestoneData.milestoneDetails.description || '',
        type: milestoneData.milestoneDetails.type,
        order: milestoneData.milestoneDetails.order || 1,
        weight: milestoneData.milestoneDetails.weight || 0
      },
      requirements: {
        verificationRequired: milestoneData.requirements?.verificationRequired ?? true,
        verificationScope: milestoneData.requirements?.verificationScope || [],
        qualityChecks: milestoneData.requirements?.qualityChecks || [],
        safetyRequirements: milestoneData.requirements?.safetyRequirements || [],
        documentRequirements: milestoneData.requirements?.documentRequirements || []
      },
      scheduling: {
        plannedStartDate: milestoneData.scheduling?.plannedStartDate || null,
        plannedEndDate: milestoneData.scheduling?.plannedEndDate || null,
        actualStartDate: null,
        actualEndDate: null,
        verificationDeadline: milestoneData.scheduling?.verificationDeadline || null
      },
      financial: {
        budgetAllocation: milestoneData.financial?.budgetAllocation || 0,
        actualCost: 0,
        paymentSchedule: milestoneData.financial?.paymentSchedule || []
      },
      verification: {
        required: milestoneData.requirements?.verificationRequired ?? true,
        requestId: null,
        status: 'not_required',
        completedAt: null,
        score: null,
        passed: null
      },
      status: 'not_started',
      progress: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const milestones = getAllMilestones();
    milestones.push(newMilestone);
    saveMilestones(milestones);
    
    return {
      success: true,
      data: newMilestone,
      message: 'Milestone created successfully!'
    };
  },

  // Get milestones by project
  getProjectMilestones: async (projectId) => {
    if (USE_API) {
      const { data } = await apiClient.get(`/projects/${projectId}/milestones`);
      return Array.isArray(data) ? data : (data.data || []);
    }
    
    const allMilestones = getAllMilestones();
    const projectMilestones = allMilestones.filter(m => m.projectId === projectId);
    
    // Sort by order
    projectMilestones.sort((a, b) => a.milestoneDetails.order - b.milestoneDetails.order);
    
    return projectMilestones;
  },

  // Get single milestone
  getMilestone: async (milestoneId) => {
    await delay(200);
    
    const allMilestones = getAllMilestones();
    const milestone = allMilestones.find(m => m.id === milestoneId);
    
    if (!milestone) {
      throw new Error('Milestone not found');
    }
    
    return milestone;
  },

  // Update milestone
  updateMilestone: async (milestoneId, updateData) => {
    await delay(400);
    
    const milestones = getAllMilestones();
    const milestoneIndex = milestones.findIndex(m => m.id === milestoneId);
    
    if (milestoneIndex === -1) {
      throw new Error('Milestone not found');
    }
    
    milestones[milestoneIndex] = {
      ...milestones[milestoneIndex],
      ...updateData,
      updatedAt: new Date().toISOString()
    };
    
    saveMilestones(milestones);
    
    return {
      success: true,
      data: milestones[milestoneIndex],
      message: 'Milestone updated successfully!'
    };
  },

  // Client approves a milestone (pending proposal or completed work)
  approveMilestone: async (milestoneId) => {
    const { data } = await apiClient.put(`/milestones/${milestoneId}/approve`);
    return data;
  },

  // Client requests changes on a milestone proposal (returns it to draft)
  rejectMilestone: async (milestoneId, reason) => {
    const { data } = await apiClient.put(`/milestones/${milestoneId}/reject`, { reason });
    return data;
  },

  // Update milestone progress
  updateMilestoneProgress: async (milestoneId, progress, actualCost = null) => {
    if (USE_API) {
      const payload = { progress, actualCost };
      const { data } = await apiClient.put(`/milestones/${milestoneId}/progress`, payload);
      return data;
    }
    
    const milestones = getAllMilestones();
    const milestoneIndex = milestones.findIndex(m => m.id === milestoneId);
    
    if (milestoneIndex === -1) {
      throw new Error('Milestone not found');
    }
    
    const milestone = milestones[milestoneIndex];
    
    // Update progress
    milestone.progress = Math.min(100, Math.max(0, progress));
    
    // Update actual cost if provided
    if (actualCost !== null) {
      milestone.financial.actualCost = actualCost;
    }
    
    // Update status based on progress
    if (milestone.progress === 0) {
      milestone.status = 'not_started';
    } else if (milestone.progress === 100) {
      milestone.status = milestone.verification.required ? 'completed' : 'verified';
      milestone.scheduling.actualEndDate = new Date().toISOString().split('T')[0];
    } else {
      milestone.status = 'in_progress';
      if (!milestone.scheduling.actualStartDate) {
        milestone.scheduling.actualStartDate = new Date().toISOString().split('T')[0];
      }
    }
    
    milestone.updatedAt = new Date().toISOString();
    saveMilestones(milestones);
    
    return {
      success: true,
      data: milestone,
      message: 'Milestone progress updated successfully!'
    };
  },

  // Start milestone verification
  startMilestoneVerification: async (milestoneId, verificationRequestId) => {
    if (USE_API) {
      const { data } = await apiClient.post(`/milestones/${milestoneId}/verification/start`, {
        verificationRequestId
      });
      return data;
    }
    
    const milestones = getAllMilestones();
    const milestoneIndex = milestones.findIndex(m => m.id === milestoneId);
    
    if (milestoneIndex === -1) {
      throw new Error('Milestone not found');
    }
    
    milestones[milestoneIndex].verification = {
      ...milestones[milestoneIndex].verification,
      requestId: verificationRequestId,
      status: 'pending'
    };
    
    milestones[milestoneIndex].updatedAt = new Date().toISOString();
    saveMilestones(milestones);
    
    return {
      success: true,
      data: milestones[milestoneIndex],
      message: 'Milestone verification started!'
    };
  },

  // Complete milestone verification
  completeMilestoneVerification: async (milestoneId, verificationScore, passed) => {
    if (USE_API) {
      const { data } = await apiClient.post(`/milestones/${milestoneId}/verification/complete`, {
        verificationScore,
        passed
      });
      return data;
    }
    
    const milestones = getAllMilestones();
    const milestoneIndex = milestones.findIndex(m => m.id === milestoneId);
    
    if (milestoneIndex === -1) {
      throw new Error('Milestone not found');
    }
    
    const milestone = milestones[milestoneIndex];
    
    // Update verification details
    milestone.verification = {
      ...milestone.verification,
      status: 'completed',
      completedAt: new Date().toISOString(),
      score: verificationScore,
      passed: passed
    };
    
    // Update milestone status
    if (passed) {
      milestone.status = 'verified';
      
      // Release payment if configured
      milestone.financial.paymentSchedule.forEach(payment => {
        if (payment.trigger === 'verification_approved' && payment.status === 'pending') {
          payment.status = 'released';
        }
      });
    } else {
      milestone.status = 'failed';
    }
    
    milestone.updatedAt = new Date().toISOString();
    saveMilestones(milestones);
    
    return {
      success: true,
      data: milestone,
      message: `Milestone verification ${passed ? 'passed' : 'failed'}!`
    };
  },

  // Get project progress summary
  getProjectProgress: async (projectId) => {
    await delay(300);
    
    const projectMilestones = await milestoneService.getProjectMilestones(projectId);
    
    if (projectMilestones.length === 0) {
      return {
        totalMilestones: 0,
        completedMilestones: 0,
        verifiedMilestones: 0,
        overallProgress: 0,
        weightedProgress: 0,
        nextMilestone: null,
        pendingVerifications: 0
      };
    }
    
    const completedMilestones = projectMilestones.filter(m => 
      m.status === 'completed' || m.status === 'verified'
    ).length;
    
    const verifiedMilestones = projectMilestones.filter(m => 
      m.status === 'verified'
    ).length;
    
    const pendingVerifications = projectMilestones.filter(m => 
      m.verification.status === 'pending' || m.verification.status === 'in_progress'
    ).length;
    
    // Calculate weighted progress
    const totalWeight = projectMilestones.reduce((sum, m) => sum + m.milestoneDetails.weight, 0);
    const weightedProgress = totalWeight > 0 
      ? projectMilestones.reduce((sum, m) => 
          sum + (m.progress * m.milestoneDetails.weight / 100), 0) / totalWeight * 100
      : 0;
    
    // Find next milestone
    const nextMilestone = projectMilestones.find(m => 
      m.status === 'not_started' || m.status === 'in_progress'
    );
    
    return {
      totalMilestones: projectMilestones.length,
      completedMilestones,
      verifiedMilestones,
      overallProgress: Math.round(completedMilestones / projectMilestones.length * 100),
      weightedProgress: Math.round(weightedProgress),
      nextMilestone,
      pendingVerifications,
      milestones: projectMilestones
    };
  },

  // Get milestones requiring verification
  getMilestonesRequiringVerification: async (filters = {}) => {
    await delay(400);
    
    let milestones = getAllMilestones();
    
    // Filter milestones that need verification
    milestones = milestones.filter(m => 
      m.verification.required && 
      (m.status === 'completed' || m.verification.status === 'pending')
    );
    
    // Apply additional filters
    if (filters.projectId) {
      milestones = milestones.filter(m => m.projectId === filters.projectId);
    }
    
    if (filters.contractorId) {
      // This would require project data to filter by contractor
      console.log('Contractor filtering not implemented - requires project service integration');
    }
    
    if (filters.priority) {
      // Sort by urgency based on deadline
      milestones.sort((a, b) => {
        const aDeadline = new Date(a.scheduling.verificationDeadline || '2099-12-31');
        const bDeadline = new Date(b.scheduling.verificationDeadline || '2099-12-31');
        return aDeadline - bDeadline;
      });
    }
    
    return milestones;
  },

  // Get milestone templates
  getMilestoneTemplates: async () => {
    await delay(200);
    
    return {
      success: true,
      data: MILESTONE_TEMPLATES,
      message: 'Milestone templates retrieved successfully!'
    };
  },

  // Delete milestone
  deleteMilestone: async (milestoneId) => {
    await delay(300);
    
    const milestones = getAllMilestones();
    const milestoneIndex = milestones.findIndex(m => m.id === milestoneId);
    
    if (milestoneIndex === -1) {
      throw new Error('Milestone not found');
    }
    
    // Check if milestone has ongoing verification
    const milestone = milestones[milestoneIndex];
    if (milestone.verification.status === 'pending' || milestone.verification.status === 'in_progress') {
      throw new Error('Cannot delete milestone with ongoing verification');
    }
    
    milestones.splice(milestoneIndex, 1);
    saveMilestones(milestones);
    
    return {
      success: true,
      message: 'Milestone deleted successfully!'
    };
  },

  // Generate milestone report
  generateMilestoneReport: async (projectId) => {
    await delay(600);
    
    const projectMilestones = await milestoneService.getProjectMilestones(projectId);
    const progressSummary = await milestoneService.getProjectProgress(projectId);
    
    const report = {
      projectId,
      summary: progressSummary,
      timeline: projectMilestones.map(m => ({
        id: m.id,
        name: m.milestoneDetails.name,
        type: m.milestoneDetails.type,
        status: m.status,
        progress: m.progress,
        plannedStart: m.scheduling.plannedStartDate,
        plannedEnd: m.scheduling.plannedEndDate,
        actualStart: m.scheduling.actualStartDate,
        actualEnd: m.scheduling.actualEndDate,
        verificationStatus: m.verification.status,
        verificationScore: m.verification.score,
        budgetAllocation: m.financial.budgetAllocation,
        actualCost: m.financial.actualCost
      })),
      financial: {
        totalBudget: projectMilestones.reduce((sum, m) => sum + m.financial.budgetAllocation, 0),
        totalActualCost: projectMilestones.reduce((sum, m) => sum + m.financial.actualCost, 0),
        releasedPayments: projectMilestones.reduce((sum, m) => 
          sum + m.financial.paymentSchedule
            .filter(p => p.status === 'released')
            .reduce((pSum, p) => pSum + p.amount, 0), 0
        ),
        pendingPayments: projectMilestones.reduce((sum, m) => 
          sum + m.financial.paymentSchedule
            .filter(p => p.status === 'pending')
            .reduce((pSum, p) => pSum + p.amount, 0), 0
        )
      },
      verification: {
        totalRequired: projectMilestones.filter(m => m.verification.required).length,
        completed: projectMilestones.filter(m => m.verification.status === 'completed').length,
        passed: projectMilestones.filter(m => m.verification.passed === true).length,
        failed: projectMilestones.filter(m => m.verification.passed === false).length,
        averageScore: projectMilestones
          .filter(m => m.verification.score !== null)
          .reduce((sum, m) => sum + m.verification.score, 0) / 
          Math.max(1, projectMilestones.filter(m => m.verification.score !== null).length)
      },
      generatedAt: new Date().toISOString()
    };
    
    return {
      success: true,
      data: report,
      message: 'Milestone report generated successfully!'
    };
  }
};

// AI-powered milestone creation methods
milestoneService.analyzeScope = async (data) => {
  if (!USE_API) {
    await delay();
    // Mock AI analysis for development
    return {
      success: true,
      data: {
        extractedKeywords: ['foundation', 'concrete', 'excavation'],
        identifiedItems: [
          { item: 'Site excavation', confidence: 0.95 },
          { item: 'Concrete pouring', confidence: 0.9 }
        ],
        suggestedItems: [
          { item: 'Waterproofing', category: 'foundation', importance: 'high', reason: 'Essential for foundation durability' },
          { item: 'Drainage systems', category: 'foundation', importance: 'medium', reason: 'Prevents water accumulation' }
        ],
        enhancedDescription: data.description,
        estimatedDuration: { min: 7, max: 14, unit: 'days' },
        suggestedDeliverables: ['Completed foundation', 'Concrete test certificates', 'Waterproofing documentation']
      }
    };
  }
  const response = await apiClient.post('/milestones-ai/analyze-scope', data);
  return response.data;
};

milestoneService.validateCosts = async (data) => {
  if (!USE_API) {
    await delay();
    return {
      success: true,
      data: {
        validation: {
          isReasonable: true,
          warnings: [],
          suggestions: ['Consider adding contingency buffer'],
          marketComparison: 'Cost is within typical market range',
          confidenceLevel: 0.85
        },
        marketComparison: {
          typicalRange: { min: 15000, max: 25000, average: 20000 },
          yourCost: data.laborCost,
          message: 'Your cost is competitive'
        },
        breakdown: {
          laborCost: data.laborCost,
          materialsCost: 0,
          totalCost: data.laborCost,
          ratePerWorkerPerDay: data.laborCost / ((data.laborDetails?.workers || 1) * (data.laborDetails?.days || 1))
        }
      }
    };
  }
  const response = await apiClient.post('/milestones-ai/validate-costs', data);
  return response.data;
};

milestoneService.generateSummary = async (data) => {
  if (!USE_API) {
    await delay();
    return {
      success: true,
      data: {
        summary: `# ${data.name}\n\n${data.description}\n\n**Total Cost:** $${data.laborCost}`
      }
    };
  }
  const response = await apiClient.post('/milestones-ai/generate-summary', data);
  return response.data;
};

milestoneService.saveDraft = async (projectId, milestoneData, conversationStep) => {
  if (!USE_API) {
    await delay();
    const draftId = `DRAFT_${Date.now()}`;
    localStorage.setItem(`milestone_draft_${projectId}`, JSON.stringify({ milestoneData, conversationStep, draftId }));
    return { success: true, data: { draftId } };
  }
  const response = await apiClient.post('/milestones-ai/draft', { projectId, milestoneData, conversationStep });
  return response.data;
};

milestoneService.getDraft = async (projectId) => {
  if (!USE_API) {
    await delay();
    const draft = localStorage.getItem(`milestone_draft_${projectId}`);
    return { success: true, data: draft ? JSON.parse(draft) : null };
  }
  const response = await apiClient.get(`/milestones-ai/draft/${projectId}`);
  return response.data;
};

milestoneService.createAIMilestone = async (milestoneData) => {
  if (!USE_API) {
    await delay();
    const milestoneId = generateMilestoneId();
    const milestone = {
      id: milestoneId,
      ...milestoneData,
      status: 'draft',
      progress: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    const milestones = getAllMilestones();
    milestones.push(milestone);
    saveMilestones(milestones);
    return { success: true, data: { milestoneId } };
  }
  const response = await apiClient.post('/milestones-ai/create', milestoneData);
  return response.data;
};

milestoneService.submitToClient = async (milestoneId) => {
  if (!USE_API) {
    await delay();
    const milestones = getAllMilestones();
    const index = milestones.findIndex(m => m.id === milestoneId);
    if (index !== -1) {
      milestones[index].status = 'pending_approval';
      milestones[index].submittedAt = new Date().toISOString();
      saveMilestones(milestones);
    }
    return { success: true, message: 'Milestone submitted for approval' };
  }
  const response = await apiClient.post(`/milestones-ai/${milestoneId}/submit`);
  return response.data;
};

// Initialize sample data when service is loaded
milestoneService.initialize();

export default milestoneService;